import DataTable from "@/components/data-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGetSpamRateData } from "@/hooks/use-postmaster";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { type ColDef } from "ag-grid-community";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const spamData = [
  { date: "Oct 15, 2024", value: 0.0 },
  { date: "Oct 16, 2024", value: 0.0 },
  { date: "Oct 17, 2024", value: 0.0 },
  { date: "Oct 18, 2024", value: 0.1 },
  { date: "Oct 19, 2024", value: 0.0 },
  { date: "Oct 20, 2024", value: 0.1 },
  { date: "Oct 21, 2024", value: 0.0 },
  { date: "Oct 22, 2024", value: 0.0 },
];

function SpamTabContent({ date, domain }: { date: string; domain: string }) {
  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const start = new Date();

    switch (date) {
      case "last7days":
        start.setDate(end.getDate() - 7);
        break;
      case "last30days":
        start.setDate(end.getDate() - 30);
        break;
      case "last60days":
        start.setDate(end.getDate() - 60);
        break;
      case "last90days":
        start.setDate(end.getDate() - 90);
        break;
      case "last120days":
        start.setDate(end.getDate() - 120);
        break;
      default:
        start.setDate(end.getDate() - 7);
    }

    return { startDate: start, endDate: end };
  }, [date]);

  const { data, isLoading } = useGetSpamRateData({
    domain,
    startDate,
    endDate,
  });

  const columnDefs: ColDef[] = [
    {
      field: "date",
      headerName: "Date",
      filter: true,
      sortable: true,
      valueFormatter: (params) =>
        format(new Date(params.value), "MMM dd, yyyy"),
    },
    {
      field: "value",
      headerName: "User reported spam rate",
      filter: true,
      sortable: true,
      valueFormatter: (params) => `${params.value.toFixed(1)}%`,
    },
  ];

  const formattedChartData = data?.chartData.map((item) => ({
    ...item,
    date: format(new Date(item.date), "MMM dd, yyyy"),
  }));

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-[50vh] w-full text-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        <Card className="shadow-none">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              Current Spam Rate
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl font-bold">
              {data?.statistics.currentSpamRate}%
            </div>
            <p className="text-xs text-muted-foreground">Last 24 hours</p>
          </CardContent>
        </Card>
        <Card className="shadow-none">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              Average Spam Rate
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl font-bold">
              {data?.statistics.averageSpamRate}%
            </div>
            <p className="text-xs text-muted-foreground">Selected period</p>
          </CardContent>
        </Card>
        <Card className="shadow-none">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">Total Reports</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl font-bold">
              {data?.statistics.totalReports}
            </div>
            <p className="text-xs text-muted-foreground">Selected period</p>
          </CardContent>
        </Card>
        <Card className="shadow-none">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div
              className={`text-sm font-medium ${
                data?.statistics.status === "Good standing"
                  ? "text-green-600"
                  : "text-yellow-600"
              }`}
            >
              {data?.statistics.status}
            </div>
            <p className="text-xs text-muted-foreground">
              {data?.statistics.status === "Good standing"
                ? "Below threshold"
                : "Above threshold"}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-none">
        <CardHeader className="p-4">
          <CardTitle>User Reported Spam</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={formattedChartData}
                margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
              >
                <defs>
                  <linearGradient id="spamGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#e5e7eb"
                />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                  tickMargin={10}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  domain={[0, "auto"]}
                  tickFormatter={(value) => `${value}%`}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--background))",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                  }}
                  formatter={(value: number) => [
                    `${value.toFixed(1)}%`,
                    "Spam Rate",
                  ]}
                  labelFormatter={(label) =>
                    format(new Date(label), "MMM dd, yyyy")
                  }
                />

                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  fill="url(#spamGradient)"
                  dot={{ stroke: "#3B82F6", strokeWidth: 2, r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="mt-6">
        <DataTable
          rowData={data?.tableData || []}
          columnDefs={columnDefs}
          height={400}
        />
      </div>
    </div>
  );
}

// Export both the component for use in other files and the Route for the router
export { SpamTabContent };

function SpamTabRouteComponent() {
  // Get domain and date from search params if available, otherwise use defaults
  const search = useSearch({ strict: false });
  const domain = (search as { domain?: string })?.domain || "";
  const date = (search as { date?: string })?.date || "last7days";
  return <SpamTabContent date={date} domain={domain} />;
}

export const Route = createFileRoute("/_private/postmaster/tabs/spam-tab")({
  component: SpamTabRouteComponent,
});
